import { createContext, useContext, useEffect, useState } from 'react';

const themeableContext = createContext<{
  isLoading: boolean;
  svgPath?: string;
}>({
  isLoading: false,
});

const FALLBACK_CSS_URL = 'https://firebasestorage.googleapis.com/v0/b/system-style/o/DYHOyEXAJXf3nWwBerbm.css?alt=media&token=6d636aa1-2812-4bdf-8377-a279c96463a5';

export const ThemeableProvider: React.FC<{
  systemId: string;
  svgPath?: string;
  children: React.ReactNode;
}> = ({ systemId, svgPath = '../icons.svg', children }) => {
  const [isReady, setIsReady] = useState(false);
  const [scopeId] = useState(() => `themeable-${Math.random().toString(36).substr(2, 9)}`);

  useEffect(() => {
    let mounted = true;
    let styleElement: HTMLStyleElement | null = null;

    // Scope CSS to the specific provider's wrapper
    const scopeCss = (css: string, scope: string) => {
      // Prepend scope to every rule, handling basic selectors
      return css
        .split('}')
        .map((rule) => {
          rule = rule.trim();
          if (!rule) return '';
          // Avoid scoping @media or @keyframes
          if (rule.startsWith('@')) return `${rule}}`;
          return `.${scope} ${rule}}`;
        })
        .join('')
        .replace(/\s+/g, ' '); // Clean up extra spaces
    };

    // Load and apply CSS
    const applyStyles = async () => {
      styleElement = document.createElement('style');
      document.head.appendChild(styleElement);

      // Step 1: Load fallback CSS first, scoped immediately
      try {
        const fallbackResponse = await fetch(FALLBACK_CSS_URL);
        const fallbackCss = await fallbackResponse.text();
        if (mounted) {
          styleElement.textContent = scopeCss(fallbackCss, scopeId);
        }
      } catch (error) {
        console.error('Failed to load fallback CSS:', error);
      }

      // Step 2: Fetch dynamic CSS if systemId exists
      if (systemId) {
        try {
          const url = `https://us-central1-themeable-ce1f4.cloudfunctions.net/styles/${systemId}?format=json`;
          const response = await fetch(url, { cache: 'default' });
          if (!response.ok) throw new Error('Failed to fetch styles');

          let cssText: string;
          // if (format === 'json') {
          if (true) {
            const data = await response.json();
            const cssResponse = await fetch(data.css);
            console.log(data)
            cssText = await cssResponse.text();
          } else {
            cssText = await response.text(); // Assumes redirect gives CSS text
          }

          if (mounted) {
            styleElement.textContent = scopeCss(cssText, scopeId);
          }
        } catch (error) {
          console.error('Error fetching styles, keeping fallback:', error);
        }
      }

      if (mounted) {
        setIsReady(true);
      }
    };

    applyStyles();

    // Cleanup
    return () => {
      mounted = false;
      if (styleElement && document.head.contains(styleElement)) {
        document.head.removeChild(styleElement);
      }
    };
  }, [systemId, scopeId]);

  return (
    <themeableContext.Provider value={{ isLoading: !isReady, svgPath }}>
      {isReady ? <div className={scopeId}>{children}</div> : null}
    </themeableContext.Provider>
  );
};

export const useThemeable = () => useContext(themeableContext);
